import { useEvent } from "@/app/hooks/use-event"
import { CloseIcon } from "@/app/icons/close"
import { ErrorCrossIcon } from "@/app/icons/error-cross"
import { LoadingSpinner } from "@/app/icons/spinner"
import { SuccessCheckIcon } from "@/app/icons/success-check"
import { WarningMarkIcon } from "@/app/icons/warning-mark"
import { Trans } from "@lingui/react/macro"
import { Button, sonnerToast } from "@rarible_int/anaconda_ui_components"
import Link from "next/link"
import type { JSX } from "react"
import { useScanner } from "../anaconda-sdk-scanner"
import { toastMainLineClassName } from "./toast-item-name"

export const AppToast = {
  Success: function ToastSuccess({
    title,
    main,
    second,
    id,
    image,
  }: {
    id: string | number
    title: string
    main: JSX.Element | null
    image?: JSX.Element
    second: JSX.Element | null
  }) {
    const handleClose = useEvent(() => {
      sonnerToast.dismiss(id)
    })
    return (
      <div
        data-testid="toast-success"
        className="flex h-[57px] max-w-[480px] items-center justify-between rounded-xl border bg-foregroundInvert/95 py-2 pr-1 pl-2 font-body shadow-custom-brand backdrop-blur-lg md:px-4 md:py-3"
      >
        <div className="flex min-w-0 items-center gap-2">
          <SuccessCheckIcon className="flex-shrink-0 flex-grow-0" />
          {image !== undefined ? <div>{image}</div> : null}

          <div className="flex min-w-0 flex-1 flex-col md:min-w-[320px]">
            <div title={title} className={toastMainLineClassName}>
              {main}
            </div>
            <div>{second}</div>
          </div>
        </div>
        <Button size="icon" variant="ghost" onClick={handleClose}>
          <CloseIcon className="[&&]:h-3 [&&]:w-3" />
        </Button>
      </div>
    )
  },
  Pending: function ToastPending({
    id,
    title,
    image,
    main,
    second,
  }: {
    id: string | number
    title: string
    main: JSX.Element
    image?: JSX.Element
    second: React.ReactNode
  }) {
    const handleClose = useEvent(() => {
      sonnerToast.dismiss(id)
    })
    return (
      <div className="flex h-[57px] max-w-[480px] items-center justify-between rounded-xl border bg-foregroundInvert/95 py-2 pr-1 pl-2 font-body shadow-custom-foreground80 backdrop-blur-lg md:px-4 md:py-3">
        <div className="flex min-w-0 items-center gap-2">
          <LoadingSpinner className="flex-shrink-0 flex-grow-0" />
          {image !== undefined ? <div>{image}</div> : null}

          <div className="flex min-w-0 flex-1 flex-col md:min-w-[320px]">
            <div title={title} className={toastMainLineClassName}>
              {main}
            </div>
            <div>{second}</div>
          </div>
        </div>
        <Button size="icon" variant="ghost" onClick={handleClose}>
          <CloseIcon className="[&&]:h-3 [&&]:w-3" />
        </Button>
      </div>
    )
  },
  Error: function ToastError({
    id,
    title,
    second,
    main,
    image,
  }: {
    id: string | number
    image?: JSX.Element
    title: string
    main: JSX.Element | null
    second: JSX.Element | null
  }) {
    const handleClose = useEvent(() => {
      sonnerToast.dismiss(id)
    })
    return (
      <div className="flex min-h-[57px] max-w-[480px] items-start justify-between rounded-xl border bg-foregroundInvert/95 py-2 pr-1 pl-2 font-body shadow-custom-destructive backdrop-blur-lg md:px-4 md:py-3">
        <div className="flex min-w-0 items-start gap-2">
          <ErrorCrossIcon className="mt-0.5 flex-shrink-0 flex-grow-0" />
          {image !== undefined ? (
            <div className="mt-0.5 flex-shrink-0">{image}</div>
          ) : null}

          <div className="flex min-w-0 flex-1 flex-col md:min-w-[320px]">
            <div
              title={title}
              className="box-border max-w-[320px] break-words font-semibold"
            >
              {main}
            </div>
            <div className="min-w-0 break-words">{second}</div>
          </div>
        </div>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleClose}
          className="mt-0.5 flex-shrink-0"
        >
          <CloseIcon className="[&&]:h-3 [&&]:w-3" />
        </Button>
      </div>
    )
  },
  Warning: function WarningToast({
    id,
    title,
    image,
    second,
    main,
  }: {
    id: string | number
    title: string
    image?: JSX.Element
    main: JSX.Element
    second: JSX.Element
  }) {
    const handleClose = useEvent(() => {
      sonnerToast.dismiss(id)
    })
    return (
      <div className="flex min-h-[57px] max-w-[480px] items-start justify-between rounded-xl border bg-foregroundInvert/95 py-2 pr-1 pl-2 font-body shadow-custom-foreground80 backdrop-blur-lg md:px-4 md:py-3">
        <div className="flex min-w-0 items-start gap-2">
          <WarningMarkIcon className="mt-0.5 flex-shrink-0 flex-grow-0" />
          {image !== undefined ? (
            <div className="mt-0.5 flex-shrink-0">{image}</div>
          ) : null}

          <div className="flex min-w-0 flex-1 flex-col md:min-w-[320px]">
            <div
              title={title}
              className="box-border max-w-[320px] break-words font-semibold"
            >
              {main}
            </div>
            <div className="min-w-0 break-words">{second}</div>
          </div>
        </div>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleClose}
          className="mt-0.5 flex-shrink-0"
        >
          <CloseIcon className="[&&]:h-3 [&&]:w-3" />
        </Button>
      </div>
    )
  },
}

interface SuccessViewTransactionProps {
  transactionId?: string
  blockchain: import("@anaconda/core/blockchain/types").Blockchain
}
export function SuccessViewTransaction({
  transactionId,
  blockchain,
}: SuccessViewTransactionProps) {
  const { getTransactionLink } = useScanner(blockchain)
  return transactionId ? (
    <div className="font-semibold text-brand">
      <Link target="_blank" href={getTransactionLink(transactionId)}>
        <Trans>View transaction</Trans>
      </Link>
    </div>
  ) : null
}
